import type { FormDefinition } from "./api";

/**
 * UI states for the visit flow.
 *
 * 1 = Pre-Visit (assigned, not clocked in)
 * 2 = Active Visit (clocked in, working — or pending correction, or clocked
 *     out with the paperwork still open: see `isClockedOutPaperworkOpen`)
 * 3 = Submitted (assessment submitted, not yet clocked out)
 * 4 = Completed (clocked out, visit done)
 */
export type VisitUIState = 1 | 2 | 3 | 4;

export function computeVisitState(visit: FormDefinition["visit"]): VisitUIState {
    // A missed visit is terminal regardless of its lifecycle status. There is no
    // `missed` status value, so a missed visit keeps its raw status (often
    // `in_progress`) and is marked only by the `missedVisit` flag. Treat it as
    // done (state 4 → read-only, no clock-out) so the RN can't keep editing the
    // assessment or clock out a visit that was already marked missed.
    if (visit.missedVisit) {
        return 4;
    }
    if (visit.openForResubmission) {
        return 2;
    }

    switch (visit.status) {
        case "assigned":
            return 1;

        case "in_progress":
            if (!visit.clockOutAt) {
                return 2;
            }
            // Clocked out with a "left before finishing" note: terminal for the
            // nurse (status deliberately stays in_progress; the admin flags it).
            // Read-only, no further clock-out.
            // Clocked out WITHOUT that note: she clocked out of it from her next
            // visit's door (one open clock at a time) with the paperwork still
            // open. She may finish it and submit; the action is a plain Submit,
            // the clock-out it has is kept.
            return visit.clockOutNote ? 4 : 2;

        case "pending_correction":
            // A reopened visit she must edit + resubmit. It carries the original
            // visit's clockOutAt, so we must NOT treat that as terminal here.
            return 2;

        case "post_processing":
        case "completed":
        case "under_review":
            // `post_processing` is a backend-only step (auto-fields + PDFs). To
            // the nurse the visit is already done — render it exactly like
            // `completed`.
            return visit.clockOutAt ? 4 : 3;

        case "sealed":
            return 4;

        default:
            // Unknown status — log warning and default to pre-visit

            console.warn(`[visit-state] Unknown visit status: "${visit.status}"`);
            return 1;
    }
}

/**
 * Whether the assessment is read-only in the current visit state.
 * State 1 (pre-visit): RN can browse but not edit
 * State 4 (completed): RN can view but not edit
 */
export function isAssessmentReadOnly(visitState: VisitUIState): boolean {
    return visitState === 1 || visitState === 4;
}

/**
 * Should her phone be capturing this visit right now (the visit recording
 * session and the GPS location trail)? Only while she is actually working it:
 * in progress, not clocked out, no open on-site issue, and not marked missed.
 *
 * Status alone is not enough. A visit marked missed after her clock-in
 * (patient refused, not home) keeps `status = in_progress` for good, so the old
 * status-only check started a recording session and a 3-hour GPS trail every
 * time she opened a visit that was already over.
 */
export function isVisitCaptureLive(
    visit: Pick<FormDefinition["visit"], "status" | "clockOutAt" | "onSiteIssue" | "missedVisit">
): boolean {
    return (
        visit.status === "in_progress" &&
        visit.clockOutAt == null &&
        !visit.onSiteIssue &&
        !visit.missedVisit
    );
}

/**
 * Clocked out, paperwork still open: an in-progress visit she clocked out of
 * without the "left before finishing" note (she clocked out of it from her next
 * visit). It stays editable, and its finish action is "Submit" only.
 */
export function isClockedOutPaperworkOpen(visit: FormDefinition["visit"]): boolean {
    return (
        visit.status === "in_progress" &&
        !visit.missedVisit &&
        visit.clockOutAt != null &&
        !visit.clockOutNote
    );
}
